import GoogleMap from 'google-map-react';
import * as React from 'react';
import { ShipmentActionType } from '../../models/enums/shipment-action-type';
import { ShipmentAction } from '../../models/shipments/shipment-action';
import { Marker, NoResultItem } from './MapComponents'; 

interface IProps {
    allStops: ShipmentAction[];
    onMarkerClick: any;
    selectedID: number;
    showRoute: boolean;
    useMiniMarker: boolean;
}

let mapReference : any = null;
let mapsReference : any = null;
let dService : any = null;
let dRenderer : any = null;

const RouteDisplay = (props : IProps) => {

    const [mapCenter] = React.useState<any>({ lat: 39.708375, lng: -88.454827 });
    const [waypoints, setWaypoints] = React.useState<any[]>([]);
    const [start, setStart] = React.useState<ShipmentAction>(new ShipmentAction());
    const [end, setEnd] = React.useState<ShipmentAction>(new ShipmentAction());
    const [init, setInit] = React.useState<boolean>(false);
    
    /**
     * On load, initialize our start
     */
    React.useEffect(() => {

        if (props.allStops) {

            const tmpList: ShipmentAction[] = [];
            const tmpWay : any[] = [];

            props.allStops.forEach((itm: ShipmentAction) => {
                switch (itm.type) {
                    case ShipmentActionType.Origin:
                        tmpWay.push({
                            latitude: itm.address.latitude,
                            longitude: itm.address.longitude,
                            label: itm.name,                    
                            show: false,
                            id: itm.id ? itm.id : itm.tmpClientId,
                            isOrigin: true
                        });
                        setStart({...itm});
                        break;
                    case ShipmentActionType.Destination:
                        setEnd({...itm});
                        break;
                    case ShipmentActionType.Delivery:
                    case ShipmentActionType.Backhaul:
                        tmpList.push(itm);
                        tmpWay.push({
                            latitude: itm.address.latitude,
                            longitude: itm.address.longitude,
                            label: itm.name,                    
                            show: false,
                            id: itm.id ? itm.id : itm.tmpClientId,
                            isOrigin: false
                        });
                        break;
                }

            });

            setWaypoints(tmpWay);
            setInit(true);
        }
    }, []);

    /**
     * When the stop list changes, we want to update
     * all our waypoints and then repaint the directions
     */
    React.useEffect(() => {
        if (props.allStops && init) {
            updateWaypoints(props.allStops);
            paintDirections(props.allStops);
        }
    }, [props.allStops]);

    /**
     * When the selected ID changes, we want to make the
     * corresponding change to the map marker
     */
    React.useEffect(() => {
        if (init) {

            const tw = [...waypoints];
            tw.forEach((way : any) => {
                way.show = (way.id === props.selectedID) ? !way.show : false;
            });

            setWaypoints(tw);
        }
    }, [props.selectedID]);

    /**
     * Update your waypoint list with the most recent version
     * @param newList The route stop list ot build our waypoint collection from
     */
    const updateWaypoints = (newList : ShipmentAction[]) => {

        if (!newList || newList.length === 0) {
            setWaypoints([]);
            return;
        }

        const newways : any[] = [];
        newways.push({
            latitude: newList[0].address.latitude,
            longitude: newList[0].address.longitude,
            label: newList[0].name,
            show: false,
            id: newList[0].id,
            isOrigin: true
        });

        newList.filter((itm : ShipmentAction) => {
            return itm.type === ShipmentActionType.Delivery || itm.type === ShipmentActionType.Backhaul;
        }).forEach((itm : ShipmentAction) => {
            newways.push({
                latitude: itm.address.latitude,
                longitude: itm.address.longitude,
                label: itm.name,
                show: false,
                id: itm.id,
                isOrigin: false
            });
        });

        setWaypoints(newways);
    };

    /**
     * Return map bounds based on list of places
     * @param map The google map instance
     * @param maps The google maps instance
     * @param places The waypoint collection to check
     */
    const getMapBounds = (map: any, maps: any, places: any) => {
        const bounds = new maps.LatLngBounds();

        places.forEach((place: any) => {
            bounds.extend(new maps.LatLng(
                place.latitude,
                place.longitude
            ));
        });
        return bounds;
    };

    /**
     * Re-center map when resizing the window
     * @param map The google map intance
     * @param maps The google maps instance
     * @param bounds The bounds of the screen
     */
    const bindResizeListener = (map: any, maps: any, bounds: any) => {
        maps.event.addDomListenerOnce(map, 'idle', () => {
            maps.event.addDomListener(window, 'resize', () => {
                map.fitBounds(bounds);
            });
        });
    };

    /**
     * Make a request to the Google Maps direction service for the
     * provided list of routes as waypoints.
     * 
     * Start and end points are pulled from the component state
     * @param routes The route list to convert to directions
     */
    const paintDirections = (routes : ShipmentAction[]) => {

        if (!routes || routes.length === 0 || props.showRoute === false ) {
            dRenderer.setMap(null);
            return;
        }

        const waypts: any[] = [];
        routes.filter((itm : ShipmentAction) => {
            return itm.type === ShipmentActionType.Delivery || itm.type === ShipmentActionType.Backhaul;
        }).forEach((itm: ShipmentAction) => {
            const t = {
                location: {
                    lat: itm.address.latitude,
                    lng: itm.address.longitude,
                    name: itm.name
                },
                stopover: false
            };

            waypts.push(t);
        });

        let endLat;
        let endLng;
        if (end.id === 0) {
            endLat = routes[routes.length - 1].address.latitude;
            endLng = routes[routes.length - 1].address.longitude;
        }
        else {
            endLat = end.address.latitude;
            endLng = end.address.longitude;
        }

        const sPoint = new mapsReference.LatLng(start.address.latitude, start.address.longitude);
        const ePoint = new mapsReference.LatLng(endLat, endLng);

        dService.route({
            origin              : sPoint,
            destination         : ePoint,
            waypoints           : waypts,
            optimizeWaypoints   : true,
            travelMode          : 'DRIVING'
        }, (response: any, status: any) => {
            if (status === 'OK') { 
                dRenderer.setMap(null);
                dRenderer.setMap(mapReference);                
                dRenderer.setDirections({
                    ...response
                });
            }
            else {
                console.error('Directions request failed due to ' + status);
            }
        });

    };

    /**
     * Callback for when the map is loaded and ready for use.
     * We size the map, subscribe to resizing and paint the
     * initial directions
     * @param map The google map instance
     * @param maps The google maps instance
     * @param places The list of waypoints to work against
     */
    const apiIsLoaded = (map: any, maps: any, places: any) => {

        mapReference = map;
        mapsReference = maps;
        dService = new mapsReference.DirectionsService();
        dRenderer = new mapsReference.DirectionsRenderer();
        dRenderer.setMap(mapReference);
        dRenderer.setOptions({
            suppressMarkers: true
        });

        // Get bounds by our places
        const bounds = getMapBounds(map, maps, places);
        // Fit map to bounds
        map.fitBounds(bounds);
        // Bind the resize listener
        bindResizeListener(map, maps, bounds);
        // Put the basic route on the map
        paintDirections(props.allStops);        
    };

    return <GoogleMap                  
                bootstrapURLKeys={{ key : 'AIzaSyAkD-OQdo2Y7Y0TlSDorQLUwDrjfyLXUFI'}}
                center={ mapCenter }
                zoom={13}
                onChildClick={ props.onMarkerClick }
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, waypoints)}
                margin={[30, 30, 30, 30]}
                hoverDistance={30}>
                {
                    waypoints && waypoints.length > 0
                    ? waypoints.map((itm: any, idx: number) => 
                        <Marker 
                            key={ itm.id }
                            useMiniMarker={ props.useMiniMarker }
                            lat={itm.latitude} 
                            lng={itm.longitude} 
                            index={ idx }
                            way={ itm } />
                    )
                    : <NoResultItem 
                        lat={mapCenter.lat} 
                        lng={ mapCenter.lng } />
                }
            </GoogleMap>;
};

RouteDisplay.defaultProps = {
    showRoute: false,
    useMiniMarker: false
};

export { RouteDisplay };
